import { TitularTooltip } from '@campfire/titular-tooltip';
import { Box, FormControl, InputLabel, Select, OutlinedInput, MenuItem, IconButton } from '@material-ui/core';
import { Clear } from '@material-ui/icons';
import { DatePicker } from '@material-ui/pickers';
import { DateTime } from 'luxon';
import React, { useState, useEffect } from 'react';

export const impactDashboardGeneralInitialStartDate = DateTime.local()
  .minus({ month: 1 })
  .startOf('month');
export const impactDashboardGeneralInitialEndDate = DateTime.local()
  .minus({ month: 1 })
  .endOf('month');

const ImpactDashboardDatePicker = ({
  value,
  onChange,
  label,
  id,
}: {
  value: DateTime;
  onChange: (value: DateTime) => void;
  label: string;
  id: string;
}) => {
  return (
    <DatePicker
      style={{ marginTop: 0, marginBottom: 0 }}
      variant='inline'
      format='dd/MM/yyyy'
      margin='normal'
      value={value}
      id={id}
      label={label}
      inputVariant='outlined'
      onChange={(date) => date && onChange(date)}
    />
  );
};

type Period = 'all-time' | 'this-month' | 'last-month' | 'this-year' | 'custom';

const PERIOD_OPTIONS: Array<{ label: string; value: Period }> = [
  { label: 'All time', value: 'all-time' },
  { label: `This month (${DateTime.local().toFormat('LLLL')})`, value: 'this-month' },
  {
    label: `Last month (${DateTime.local()
      .minus({ month: 1 })
      .toFormat('LLLL')})`,
    value: 'last-month',
  },
  { label: 'This year', value: 'this-year' },
  { label: 'Custom', value: 'custom' },
];

interface Props {
  endDate: DateTime;
  setEndDate: (value: DateTime) => void;
  setStartDate: (value: DateTime) => void;
  startDate: DateTime;
}

const ImpactDashboardGeneralDateFilter = (props: Props) => {
  const { endDate, setEndDate, setStartDate, startDate } = props;
  const [period, setPeriod] = useState<Period>(PERIOD_OPTIONS[2].value);

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setPeriod(event.target.value as Period);
  };

  useEffect(() => {
    if (period === 'all-time') {
      setStartDate(DateTime.local(2005));
      setEndDate(DateTime.local().endOf('year'));
      return;
    }

    if (period === 'this-month') {
      setStartDate(DateTime.local().startOf('month'));
      setEndDate(DateTime.local().endOf('month'));
      return;
    }

    if (period === 'last-month') {
      setStartDate(
        DateTime.local()
          .minus({ month: 1 })
          .startOf('month')
      );
      setEndDate(
        DateTime.local()
          .minus({ month: 1 })
          .endOf('month')
      );
      return;
    }

    if (period === 'this-year') {
      setStartDate(DateTime.local().startOf('year'));
      setEndDate(DateTime.local().endOf('year'));
    }
  }, [period]);

  return (
    <Box display='flex'>
      {period === 'custom' ? (
        <Box
          display='flex'
          alignContent='center'
          alignItems='center'
          // marginTop={'-24px'}
        >
          <Box marginRight={1}>
            <ImpactDashboardDatePicker label='Start Date' id='start-date' value={startDate} onChange={setStartDate} />
          </Box>
          <Box marginRight={1}>
            <ImpactDashboardDatePicker label='End Date' id='end-date' value={endDate} onChange={setEndDate} />
          </Box>
          <Box marginRight={1}>
            <TitularTooltip description='Back to last month'>
              <IconButton onClick={() => setPeriod('last-month')}>
                <Clear />
              </IconButton>
            </TitularTooltip>
          </Box>
        </Box>
      ) : (
        <Box width={196}>
          <FormControl fullWidth variant='outlined'>
            <InputLabel id='impact-dashboard-date-range-select-label'>{'Period'}</InputLabel>
            <Select
              labelId='impact-dashboard-date-range-select-label'
              id='impact-dashboard-date-range-select'
              value={period}
              onChange={handleChange}
              input={
                <OutlinedInput
                  labelWidth={48}
                  name='impact-dashboard-date-range'
                  id='impact-dashboard-date-range-select'
                />
              }
            >
              {PERIOD_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}
    </Box>
  );
};

export { ImpactDashboardGeneralDateFilter };
